import { SyncOperation } from "../shared/api.js";
import { Profile } from "../shared/type.app.js";

const DATABASE_NAME = "reading-bee";
const DATABASE_VERSION = 2;
const OUTBOX_STORE = "outbox";
const PROFILE_STORE = "profiles";
const SHARED_STUDENT_STORE = "sharedStudents";
const APP_STORE = "app";
const SYNC_META_STORE = "syncMeta";
const PROFILE_VIEW_STORE = "profileViews";

export type OutboxRecord = {
  eventId: string;
  operation: SyncOperation;
  createdAt: string;
  attempts: number;
  nextAttemptAt: number;
  lastError: string | null;
};

export type AppRecord = {
  key: "state" | "clientId";
  currentProfileId?: string | null;
  passcode?: string | null;
  clientId?: string;
};

const requestResult = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });

const transactionComplete = (transaction: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
  });

const openDatabase = (): Promise<IDBDatabase> => {
  if (typeof indexedDB === "undefined") throw new Error("IndexedDB is unavailable");
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(OUTBOX_STORE)) {
        const store = database.createObjectStore(OUTBOX_STORE, { keyPath: "eventId" });
        store.createIndex("ready", "nextAttemptAt", { unique: false });
      }
      if (!database.objectStoreNames.contains(PROFILE_STORE))
        database.createObjectStore(PROFILE_STORE, { keyPath: "id" });
      if (!database.objectStoreNames.contains(SHARED_STUDENT_STORE))
        database.createObjectStore(SHARED_STUDENT_STORE, { keyPath: "shareId" });
      if (!database.objectStoreNames.contains(APP_STORE)) database.createObjectStore(APP_STORE, { keyPath: "key" });
      if (!database.objectStoreNames.contains(SYNC_META_STORE))
        database.createObjectStore(SYNC_META_STORE, { keyPath: "key" });
      if (!database.objectStoreNames.contains(PROFILE_VIEW_STORE))
        database.createObjectStore(PROFILE_VIEW_STORE, { keyPath: ["studentId", "detailVersion"] });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Could not open IndexedDB"));
  });
};

const errorMessage = (error: unknown): string => (error instanceof Error ? error.message : "Sync failed");

export class SqlOutbox extends EventTarget {
  private readonly database = openDatabase();

  async persistWithProjection(
    operations: SyncOperation[],
    profiles: Profile[],
    currentProfileId: string | null,
    incomingShares: Array<Record<string, unknown>>,
  ): Promise<void> {
    if (operations.length === 0) return;
    const database = await this.database;
    const transaction = database.transaction(
      [OUTBOX_STORE, PROFILE_STORE, SHARED_STUDENT_STORE, APP_STORE],
      "readwrite",
    );
    const outboxStore = transaction.objectStore(OUTBOX_STORE);
    const profileStore = transaction.objectStore(PROFILE_STORE);
    const sharedStore = transaction.objectStore(SHARED_STUDENT_STORE);
    for (const profile of profiles) profileStore.put(profile);
    for (const share of incomingShares) {
      const shareId = typeof share.shareId === "string" ? share.shareId : String(share.profileId ?? "");
      if (shareId) sharedStore.put({ ...share, shareId });
    }
    transaction.objectStore(APP_STORE).put({ key: "state", currentProfileId } satisfies AppRecord);
    for (const operation of operations) {
      outboxStore.put({
        eventId: operation.eventId,
        operation,
        createdAt: new Date().toISOString(),
        attempts: 0,
        nextAttemptAt: 0,
        lastError: null,
      } satisfies OutboxRecord);
    }
    await transactionComplete(transaction);
    this.dispatchEvent(new Event("change"));
  }

  async enqueue(operation: SyncOperation): Promise<void> {
    const database = await this.database;
    const transaction = database.transaction(OUTBOX_STORE, "readwrite");
    transaction.objectStore(OUTBOX_STORE).put({
      eventId: operation.eventId,
      operation,
      createdAt: new Date().toISOString(),
      attempts: 0,
      nextAttemptAt: 0,
      lastError: null,
    } satisfies OutboxRecord);
    await transactionComplete(transaction);
    this.dispatchEvent(new Event("change"));
  }

  async ready(limit = 500, now = Date.now()): Promise<OutboxRecord[]> {
    const database = await this.database;
    const transaction = database.transaction(OUTBOX_STORE, "readonly");
    const records = (await requestResult(transaction.objectStore(OUTBOX_STORE).getAll())) as OutboxRecord[];
    await transactionComplete(transaction);
    return records
      .filter((record) => record.nextAttemptAt <= now)
      .sort((left, right) => left.createdAt.localeCompare(right.createdAt))
      .slice(0, limit);
  }

  async readyBatch(limit = 500, maxBytes = 256 * 1024, now = Date.now()): Promise<OutboxRecord[]> {
    const records = await this.ready(limit, now);
    const selected: OutboxRecord[] = [];
    let size = 2;
    for (const record of records) {
      const recordSize = JSON.stringify(record.operation).length;
      if (selected.length > 0 && size + recordSize > maxBytes) break;
      selected.push(record);
      size += recordSize;
    }
    return selected;
  }

  async pending(): Promise<OutboxRecord[]> {
    const database = await this.database;
    const transaction = database.transaction(OUTBOX_STORE, "readonly");
    const records = (await requestResult(transaction.objectStore(OUTBOX_STORE).getAll())) as OutboxRecord[];
    await transactionComplete(transaction);
    return records;
  }

  async retainOperationsForStudents(studentIds: Set<string>): Promise<void> {
    const database = await this.database;
    const transaction = database.transaction(OUTBOX_STORE, "readwrite");
    const store = transaction.objectStore(OUTBOX_STORE);
    const records = (await requestResult(store.getAll())) as OutboxRecord[];
    for (const record of records) {
      if (!studentIds.has(record.operation.studentId)) store.delete(record.eventId);
    }
    await transactionComplete(transaction);
    this.dispatchEvent(new Event("change"));
  }

  async replaceLocalData(
    profiles: Profile[],
    incomingShares: Array<Record<string, unknown>>,
    state: { currentProfileId: string | null; passcode: string | null },
    operations: SyncOperation[],
  ): Promise<void> {
    const database = await this.database;
    const transaction = database.transaction(
      [OUTBOX_STORE, PROFILE_STORE, SHARED_STUDENT_STORE, APP_STORE],
      "readwrite",
    );
    const outboxStore = transaction.objectStore(OUTBOX_STORE);
    const profileStore = transaction.objectStore(PROFILE_STORE);
    const sharedStore = transaction.objectStore(SHARED_STUDENT_STORE);
    outboxStore.clear();
    profileStore.clear();
    sharedStore.clear();
    for (const profile of profiles) profileStore.put(profile);
    for (const share of incomingShares) {
      const shareId = typeof share.shareId === "string" ? share.shareId : String(share.profileId ?? "");
      if (shareId) sharedStore.put({ ...share, shareId });
    }
    transaction.objectStore(APP_STORE).put({ key: "state", ...state } satisfies AppRecord);
    for (const operation of operations) {
      outboxStore.put({
        eventId: operation.eventId,
        operation,
        createdAt: new Date().toISOString(),
        attempts: 0,
        nextAttemptAt: 0,
        lastError: null,
      } satisfies OutboxRecord);
    }
    await transactionComplete(transaction);
    this.dispatchEvent(new Event("change"));
  }

  async clearLocalData(): Promise<void> {
    const database = await this.database;
    const transaction = database.transaction(
      [OUTBOX_STORE, PROFILE_STORE, SHARED_STUDENT_STORE, APP_STORE, SYNC_META_STORE, PROFILE_VIEW_STORE],
      "readwrite",
    );
    for (const name of [
      OUTBOX_STORE,
      PROFILE_STORE,
      SHARED_STUDENT_STORE,
      APP_STORE,
      SYNC_META_STORE,
      PROFILE_VIEW_STORE,
    ])
      transaction.objectStore(name).clear();
    await transactionComplete(transaction);
    this.dispatchEvent(new Event("change"));
  }

  async removeProfileData(profileId: string): Promise<void> {
    const database = await this.database;
    const transaction = database.transaction([OUTBOX_STORE, PROFILE_STORE, SHARED_STUDENT_STORE], "readwrite");
    transaction.objectStore(PROFILE_STORE).delete(profileId);
    const sharedStore = transaction.objectStore(SHARED_STUDENT_STORE);
    const shares = (await requestResult(sharedStore.getAll())) as Array<Record<string, unknown>>;
    for (const share of shares) {
      if (String(share.profileId ?? "") === profileId && typeof share.shareId === "string") {
        sharedStore.delete(share.shareId);
      }
    }
    const outboxStore = transaction.objectStore(OUTBOX_STORE);
    const records = (await requestResult(outboxStore.getAll())) as OutboxRecord[];
    for (const record of records) {
      if (record.operation.studentId === profileId) outboxStore.delete(record.eventId);
    }
    await transactionComplete(transaction);
    this.dispatchEvent(new Event("change"));
  }

  async pendingStudentIds(): Promise<Set<string>> {
    const records = await this.pending();
    return new Set(records.map((record) => record.operation.studentId));
  }

  async clientId(): Promise<string> {
    const database = await this.database;
    const transaction = database.transaction(APP_STORE, "readwrite");
    const store = transaction.objectStore(APP_STORE);
    const existing = (await requestResult(store.get("clientId"))) as AppRecord | undefined;
    if (existing?.clientId) {
      await transactionComplete(transaction);
      return existing.clientId;
    }
    const clientId = crypto.randomUUID();
    store.put({ key: "clientId", clientId } satisfies AppRecord);
    await transactionComplete(transaction);
    return clientId;
  }

  async acknowledge(eventIds: string[]): Promise<void> {
    if (eventIds.length === 0) return;
    const database = await this.database;
    const transaction = database.transaction(OUTBOX_STORE, "readwrite");
    const store = transaction.objectStore(OUTBOX_STORE);
    for (const eventId of eventIds) store.delete(eventId);
    await transactionComplete(transaction);
    this.dispatchEvent(new Event("change"));
  }

  async defer(eventIds: string[], error: unknown, now = Date.now()): Promise<void> {
    if (eventIds.length === 0) return;
    const database = await this.database;
    const transaction = database.transaction(OUTBOX_STORE, "readwrite");
    const store = transaction.objectStore(OUTBOX_STORE);
    for (const eventId of eventIds) {
      const request = store.get(eventId);
      request.onsuccess = () => {
        const record = request.result as OutboxRecord | undefined;
        if (!record) return;
        const attempts = record.attempts + 1;
        store.put({
          ...record,
          attempts,
          nextAttemptAt: now + Math.min(15 * 60_000, 1_000 * 2 ** Math.min(attempts, 10)),
          lastError: errorMessage(error),
        } satisfies OutboxRecord);
      };
    }
    await transactionComplete(transaction);
    this.dispatchEvent(new Event("change"));
  }

  async count(): Promise<number> {
    const database = await this.database;
    const transaction = database.transaction(OUTBOX_STORE, "readonly");
    const count = await requestResult(transaction.objectStore(OUTBOX_STORE).count());
    await transactionComplete(transaction);
    return count;
  }
}
